import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React, {useEffect, useState} from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/ai-prompts')({
    component: RouteComponent,
})

type AgentPrompt = {
    name: string;
    label: string;
    file_default: string;
    override: string | null;
    is_overridden: boolean;
    updated_at?: string | null;
};

function AgentEditor({agent, onSaved}: {agent: AgentPrompt; onSaved: () => void}) {
    const [text, setText] = useState(agent.override ?? agent.file_default);
    const dirty = text !== (agent.override ?? agent.file_default);

    // Refresh the editor when the server copy changes (e.g. after reset).
    useEffect(() => { setText(agent.override ?? agent.file_default); }, [agent.override, agent.file_default]);

    const save = useMutation({
        mutationFn: () => apiActionImproved('master/ai-prompts/save' as any, {agent_name: agent.name, instructions: text}),
        onSuccess:  () => { addToast({title: 'Prompt saved — live within 5 minutes (cache)', color: 'success'}); onSaved(); },
        onError:    (e: any) => addToast({title: e?.message || 'Could not save the prompt', color: 'danger'}),
    });
    const reset = useMutation({
        mutationFn: () => apiActionImproved('master/ai-prompts/reset' as any, {agent_name: agent.name}),
        onSuccess:  () => { addToast({title: 'Reverted to the file default', color: 'success'}); onSaved(); },
        onError:    (e: any) => addToast({title: e?.message || 'Could not reset the prompt', color: 'danger'}),
    });

    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">{agent.label}</h3>
                {agent.is_overridden
                    ? <Chip color="primary" size="sm" variant="flat">Database override active</Chip>
                    : <Chip color="default" size="sm" variant="flat">Using file default</Chip>}
                {agent.updated_at && <span className="text-xs text-default-400">updated {new Date(agent.updated_at).toLocaleString()}</span>}
            </div>
            <p className="mt-1 text-xs text-default-500">
                Saving stores the prompt in the database and it goes live without a deploy (allow up to 5 minutes for the cache).
                "Revert to file default" removes the override so the agent uses the prompt shipped with the code.
            </p>

            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                spellCheck={false}
                className="mt-3 h-[480px] w-full resize-y rounded-lg border border-default-200 bg-default-50 p-3 font-mono text-xs leading-relaxed outline-none focus:border-primary"
            />
            <div className="mt-1 text-right text-xs text-default-400">{text.length.toLocaleString()} characters</div>

            <div className="mt-2 flex flex-wrap gap-2">
                <PButton size="sm" color="primary" isDisabled={!dirty || text.trim().length < 50} isLoading={save.isPending}
                         startContent={<Icon icon="material-symbols-light:save-outline" className="text-lg" />}
                         onPress={() => save.mutate()}>Save & publish</PButton>
                <PButton size="sm" variant="flat" isDisabled={!dirty}
                         onPress={() => setText(agent.override ?? agent.file_default)}>Discard changes</PButton>
                {agent.is_overridden && (
                    <PButton size="sm" color="warning" variant="flat" isLoading={reset.isPending}
                             startContent={<Icon icon="material-symbols-light:history" className="text-lg" />}
                             onPress={() => reset.mutate()}>Revert to file default</PButton>
                )}
            </div>
        </div>
    );
}

function RouteComponent() {
    const queryClient = useQueryClient();

    const query = useQuery<{agents: AgentPrompt[]}>({
        queryKey: ['master-ai-prompts'],
        queryFn:  () => apiRetrieveImproved('master/ai-prompts' as any),
    });

    const refresh = () => queryClient.invalidateQueries({queryKey: ['master-ai-prompts']});
    const agents = query.data?.agents || [];

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-5xl py-4">
                <h1 className="mb-1 text-2xl font-bold">AI Prompts</h1>
                <p className="mb-4 text-sm text-default-500">
                    Edit the instructions behind the AI assistants. The active prompt is what the model reads on every question.
                </p>

                {query.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : agents.length === 0 ? (
                    <div className="py-12 text-center text-default-500">No editable agents.</div>
                ) : (
                    <div className="grid gap-4">
                        {agents.map((a) => <AgentEditor key={a.name} agent={a} onSaved={refresh} />)}
                    </div>
                )}
            </div>
        </>
    );
}
